import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAgency } from '../context/AgencyContext';
import { listAgencyBusinessCalls } from '../services/api';
import { format } from 'date-fns';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

interface CallLog {
  id: string;
  callerNumber: string;
  calledNumber: string;
  status: string;
  duration?: number;
  extractedData?: {
    callerName?: string;
    callerEmail?: string;
    appointmentDate?: string;
  };
  createdAt?: { toDate?: () => Date; seconds?: number };
}

export default function CallsPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { currentAgency, businesses } = useAgency();
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const business = businesses.find(b => b.id === businessId);

  const fetchCalls = async (startAfter?: string) => {
    if (!currentAgency || !businessId) return;

    setLoading(true);
    try {
      const data = await listAgencyBusinessCalls(currentAgency.id, businessId, 25, startAfter);
      if (startAfter) {
        setCalls((prev) => [...prev, ...data]);
      } else {
        setCalls(data);
      }
      setHasMore(data.length === 25);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [currentAgency, businessId]);

  const loadMore = () => {
    if (calls.length > 0) {
      fetchCalls(calls[calls.length - 1].id);
    }
  };

  const formatDate = (createdAt: CallLog['createdAt']) => {
    if (!createdAt) return 'Unknown';
    if (createdAt.toDate) {
      return format(createdAt.toDate(), 'MMM d, yyyy h:mm a');
    }
    if (createdAt.seconds) {
      return format(new Date(createdAt.seconds * 1000), 'MMM d, yyyy h:mm a');
    }
    return 'Unknown';
  };

  if (!currentAgency || !businessId) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate(`/clients/${businessId}`)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to {business?.name || 'Client'}
        </button>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Call Logs {business && <span className="text-gray-500 font-normal">- {business.name}</span>}
      </h1>

      <div className="bg-white shadow rounded-lg">
        {loading && calls.length === 0 ? (
          <div className="p-6 animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : calls.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No calls recorded yet</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Caller</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Info Collected</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {calls.map((call) => (
                    <tr key={call.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900">{call.callerNumber}</p>
                          {call.extractedData?.callerName && (
                            <p className="text-gray-500 text-xs">
                              {call.extractedData.callerName}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(call.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {call.duration
                          ? `${Math.floor(call.duration / 60)}:${String(
                              call.duration % 60
                            ).padStart(2, '0')}`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            call.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : call.status === 'in-progress'
                              ? 'bg-blue-100 text-blue-800'
                              : call.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {call.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-1 flex-wrap">
                          {call.extractedData?.callerName && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                              Name
                            </span>
                          )}
                          {call.extractedData?.callerEmail && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700">
                              Email
                            </span>
                          )}
                          {call.extractedData?.appointmentDate && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700">
                              Appt
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/clients/${businessId}/calls/${call.id}`}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="p-4 text-center border-t">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
